import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { InterestCategory, InterestCategoryNode, InterestCategoryFormData } from '../types';
import { getUserData } from '../utils';

interface InterestCategoryStore {
  // State
  categories: InterestCategory[];
  categoryTree: InterestCategoryNode[];
  loading: boolean;
  // true solo dopo un fetch andato a buon fine (per il pruning dei filtri:
  // distingue lista vuota reale da lista non ancora caricata/fetch fallita)
  fetched: boolean;
  error: string | null;
  expandedIds: Set<string>;

  // CRUD Actions
  fetchCategories: () => Promise<void>;
  createCategory: (category: InterestCategoryFormData) => Promise<InterestCategory | null>;
  createCategoryInline: (name: string, parentId: string | null) => Promise<InterestCategory | null>;
  updateCategory: (id: string, category: Partial<InterestCategoryFormData>) => Promise<InterestCategory | null>;
  deleteCategory: (id: string) => Promise<boolean>;
  bulkDeleteCategories: (ids: string[]) => Promise<{ successCount: number; errorCount: number; errors: string[] }>;

  // Tree Operations
  buildTree: () => void;
  moveCategory: (categoryId: string, newParentId: string | null) => Promise<boolean>;
  getDescendantIds: (categoryId: string) => string[];
  getAncestorIds: (categoryId: string) => string[];
  findCategoryById: (categoryId: string) => InterestCategory | undefined;
  validateCategoryMove: (categoryId: string, newParentId: string | null) => boolean;

  // UI State
  toggleExpanded: (categoryId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  setExpandedIds: (ids: Set<string>) => void;
}

export const useInterestCategoryStore = create<InterestCategoryStore>((set, get) => ({
  categories: [],
  categoryTree: [],
  loading: false,
  fetched: false,
  error: null,
  expandedIds: new Set<string>(),

  // ============== CRUD Actions ==============

  fetchCategories: async () => {
    set({ loading: true, error: null });
    try {
      const userData = await getUserData();

      if (!userData?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Fetch all categories
      const { data: categories, error: categoriesError } = await supabase
        .from('interest_categories')
        .select('*')
        .eq('organization_id', userData.organizationId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (categoriesError) {
        throw categoriesError;
      }

      // Assicurati che level abbia un valore di default
      const categoriesWithDefaults = (categories || []).map(cat => ({
        ...cat,
        level: cat.level ?? 0
      }));

      set({ categories: categoriesWithDefaults, loading: false, fetched: true });

      // Build tree after fetching
      get().buildTree();
    } catch (error) {
      console.error('Error fetching interest categories:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  createCategory: async (categoryData) => {
    try {
      const userData = await getUserData();

      if (!userData?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Calcola il sort_order per nuove categorie
      const { categories } = get();
      const siblingsCount = categories.filter(c => c.parent_id === categoryData.parent_id).length;

      const insertData: Record<string, unknown> = {
        name: categoryData.name,
        description: categoryData.description || null,
        parent_id: categoryData.parent_id || null,
        color: categoryData.color || '#3B82F6',
        sort_order: categoryData.sort_order ?? siblingsCount,
        is_active: categoryData.is_active ?? true,
        organization_id: userData.organizationId,
        created_by: userData.userId,
        updated_by: userData.userId
      };

      // Mantieni category_type per backward compatibility se fornito
      if (categoryData.category_type) {
        insertData.category_type = categoryData.category_type;
      }

      const { data, error } = await supabase
        .from('interest_categories')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Refresh categories
      await get().fetchCategories();

      // Espandi il parent se esiste
      if (data.parent_id) {
        const { expandedIds } = get();
        const newExpandedIds = new Set(expandedIds);
        newExpandedIds.add(data.parent_id);
        set({ expandedIds: newExpandedIds });
      }

      return data;
    } catch (error) {
      console.error('Error creating interest category:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  createCategoryInline: async (name: string, parentId: string | null) => {
    // Versione semplificata per creazione rapida inline
    return get().createCategory({
      name,
      parent_id: parentId,
      color: '#3B82F6',
      is_active: true
    });
  },

  updateCategory: async (id, categoryData) => {
    try {
      const userData = await getUserData();
      if (!userData?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Validate: una categoria non può essere parent di se stessa
      if (categoryData.parent_id === id) {
        throw new Error('Una categoria non può essere padre di se stessa');
      }

      // Validate: non può essere spostata sotto un suo discendente
      if (categoryData.parent_id) {
        const descendantIds = get().getDescendantIds(id);
        if (descendantIds.includes(categoryData.parent_id)) {
          throw new Error('Una categoria non può essere spostata sotto un suo discendente');
        }
      }

      const updateData: Record<string, unknown> = {
        updated_by: userData.userId
      };

      if (categoryData.name !== undefined) updateData.name = categoryData.name;
      if (categoryData.description !== undefined) updateData.description = categoryData.description;
      if (categoryData.parent_id !== undefined) updateData.parent_id = categoryData.parent_id;
      if (categoryData.color !== undefined) updateData.color = categoryData.color;
      if (categoryData.sort_order !== undefined) updateData.sort_order = categoryData.sort_order;
      if (categoryData.is_active !== undefined) updateData.is_active = categoryData.is_active;

      const { data, error } = await supabase
        .from('interest_categories')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Refresh categories
      await get().fetchCategories();

      return data;
    } catch (error) {
      console.error('Error updating interest category:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteCategory: async (id) => {
    try {
      const userData = await getUserData();
      if (!userData?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Trova tutti i discendenti da eliminare
      const descendantIds = get().getDescendantIds(id);
      const idsToDelete = [id, ...descendantIds];

      // Elimina tutte le categorie (discendenti + la categoria stessa)
      const { error } = await supabase
        .from('interest_categories')
        .delete()
        .in('id', idsToDelete)
        .eq('organization_id', userData.organizationId);

      if (error) {
        throw error;
      }

      // Refresh categories
      await get().fetchCategories();

      return true;
    } catch (error) {
      console.error('Error deleting interest category:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  bulkDeleteCategories: async (ids) => {
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      const userData = await getUserData();
      if (!userData?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Raccogli tutti gli ID inclusi i discendenti
      const allIdsToDelete = new Set<string>();
      for (const id of ids) {
        allIdsToDelete.add(id);
        const descendantIds = get().getDescendantIds(id);
        descendantIds.forEach(descId => allIdsToDelete.add(descId));
      }

      // Elimina tutte le categorie in batch
      const { error: batchDeleteError } = await supabase
        .from('interest_categories')
        .delete()
        .in('id', Array.from(allIdsToDelete))
        .eq('organization_id', userData.organizationId);

      if (batchDeleteError) {
        throw batchDeleteError;
      }

      successCount = allIdsToDelete.size;

      // Refresh categories
      await get().fetchCategories();

      return { successCount, errorCount, errors };
    } catch (error) {
      console.error('Error bulk deleting categories:', error);
      errorCount = ids.length;
      errors.push(friendlyDbError(error));
      set({ error: friendlyDbError(error) });
      return { successCount, errorCount, errors };
    }
  },

  // ============== Tree Operations ==============

  buildTree: () => {
    const { categories } = get();

    const buildNode = (category: InterestCategory): InterestCategoryNode => ({
      ...category,
      children: categories
        .filter(c => c.parent_id === category.id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(buildNode)
    });

    // Root nodes = categorie senza parent_id
    const tree = categories
      .filter(c => !c.parent_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(buildNode);

    set({ categoryTree: tree });
  },

  moveCategory: async (categoryId: string, newParentId: string | null) => {
    // Valida lo spostamento
    if (!get().validateCategoryMove(categoryId, newParentId)) {
      return false;
    }

    // Aggiorna la categoria
    const result = await get().updateCategory(categoryId, { parent_id: newParentId });
    return result !== null;
  },

  getDescendantIds: (categoryId: string): string[] => {
    const { categories } = get();
    const descendants: string[] = [];

    const collectDescendants = (parentId: string) => {
      const children = categories.filter(c => c.parent_id === parentId);
      for (const child of children) {
        descendants.push(child.id);
        collectDescendants(child.id);
      }
    };

    collectDescendants(categoryId);
    return descendants;
  },

  getAncestorIds: (categoryId: string): string[] => {
    const { categories } = get();
    const ancestors: string[] = [];

    let currentCategory = categories.find(c => c.id === categoryId);
    while (currentCategory?.parent_id) {
      ancestors.push(currentCategory.parent_id);
      currentCategory = categories.find(c => c.id === currentCategory?.parent_id);
    }

    return ancestors;
  },

  findCategoryById: (categoryId: string): InterestCategory | undefined => {
    return get().categories.find(c => c.id === categoryId);
  },

  validateCategoryMove: (categoryId: string, newParentId: string | null): boolean => {
    // Non può essere parent di se stessa
    if (categoryId === newParentId) {
      return false;
    }

    // Non può essere spostata sotto un suo discendente
    if (newParentId) {
      const descendantIds = get().getDescendantIds(categoryId);
      if (descendantIds.includes(newParentId)) {
        return false;
      }
    }

    return true;
  },

  // ============== UI State ==============

  toggleExpanded: (categoryId: string) => {
    const { expandedIds } = get();
    const newExpandedIds = new Set(expandedIds);

    if (newExpandedIds.has(categoryId)) {
      newExpandedIds.delete(categoryId);
    } else {
      newExpandedIds.add(categoryId);
    }

    set({ expandedIds: newExpandedIds });
  },

  expandAll: () => {
    const { categories } = get();
    // Espandi tutte le categorie che hanno figli
    const categoriesWithChildren = categories.filter(c =>
      categories.some(child => child.parent_id === c.id)
    );
    set({ expandedIds: new Set(categoriesWithChildren.map(c => c.id)) });
  },

  collapseAll: () => {
    set({ expandedIds: new Set() });
  },

  setExpandedIds: (ids: Set<string>) => {
    set({ expandedIds: ids });
  }
}));
